import { useState } from "react";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Project } from "@/services/project.service";
import {
	createMcpConnection,
	updateMcpConnection,
	type CreatedMcpConnection,
	type McpClient,
	type McpConnection,
	type McpPermission,
} from "@/services/mcp-connection.service";
import { CLIENT_LABELS, READ_PERMISSIONS } from "./mcp-setup";
import { McpPermissionFields } from "./McpPermissionFields";

const SELECT_CLS =
	"h-9 w-full rounded-lg border border-border-strong bg-surface px-3 text-sm text-foreground";

interface DialogProps {
	open: boolean;
	editing: McpConnection | null;
	projects: Project[];
	onOpenChange: (open: boolean) => void;
	onCreated: (c: CreatedMcpConnection) => void;
	onUpdated: (c: McpConnection) => void;
}

// Create when `editing` is null, otherwise edit name/permissions/projects.
export function McpConnectionDialog({ open, editing, onOpenChange, ...rest }: DialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{editing ? "Edit connection" : "New MCP connection"}</DialogTitle>
					<DialogDescription>
						{editing
							? "Changes apply to the agent's next request."
							: "Lets an AI agent work in TaskHub as you. The token is shown once."}
					</DialogDescription>
				</DialogHeader>
				{/* Keyed + mounted only while open, so state starts fresh each time. */}
				{open && (
					<ConnectionForm
						key={editing?.id ?? "new"}
						editing={editing}
						onClose={() => onOpenChange(false)}
						{...rest}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

function ConnectionForm({
	editing,
	projects,
	onClose,
	onCreated,
	onUpdated,
}: Omit<DialogProps, "open" | "onOpenChange"> & { onClose: () => void }) {
	const [name, setName] = useState(editing?.name ?? "");
	const [client, setClient] = useState<McpClient>("claude");
	const [permissions, setPermissions] = useState<McpPermission[]>(
		editing?.permissions ?? READ_PERMISSIONS,
	);
	const [projectIds, setProjectIds] = useState<string[]>(editing?.project_ids ?? []);
	const [expiresAt, setExpiresAt] = useState("");
	const [submitting, setSubmitting] = useState(false);

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		if (name.trim().length < 2 || permissions.length === 0) {
			toast.error("Name and at least one permission are required.");
			return;
		}
		setSubmitting(true);
		try {
			const base = { name: name.trim(), permissions, project_ids: projectIds };
			if (editing) {
				onUpdated(await updateMcpConnection(editing.id, base));
				toast.success("Connection updated.");
			} else {
				onCreated(
					await createMcpConnection({
						...base,
						client,
						expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
					}),
				);
			}
			onClose();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to save connection.");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form onSubmit={submit} className="space-y-4">
			<div className="grid gap-4 sm:grid-cols-2">
				<div className="space-y-1.5">
					<label htmlFor="mcp-name" className="text-xs font-medium text-muted-foreground">
						Name
					</label>
					<Input
						id="mcp-name"
						placeholder="e.g. Claude Code on laptop"
						value={name}
						onChange={(e) => setName(e.target.value)}
						maxLength={50}
					/>
				</div>
				{!editing && (
					<div className="space-y-1.5">
						<label htmlFor="mcp-client" className="text-xs font-medium text-muted-foreground">
							Agent
						</label>
						<select
							id="mcp-client"
							value={client}
							onChange={(e) => setClient(e.target.value as McpClient)}
							className={SELECT_CLS}
						>
							{(Object.keys(CLIENT_LABELS) as McpClient[]).map((c) => (
								<option key={c} value={c}>
									{CLIENT_LABELS[c]}
								</option>
							))}
						</select>
					</div>
				)}
			</div>

			<McpPermissionFields
				permissions={permissions}
				onPermissionsChange={setPermissions}
				projectIds={projectIds}
				onProjectIdsChange={setProjectIds}
				projects={projects}
			/>

			{!editing && (
				<div className="space-y-1.5">
					<label htmlFor="mcp-expires" className="text-xs font-medium text-muted-foreground">
						Expires at (optional)
					</label>
					<Input id="mcp-expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
				</div>
			)}

			<DialogFooter>
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Cancel
				</Button>
				<Button type="submit" size="sm" disabled={submitting}>
					{submitting ? "Saving…" : editing ? "Save" : "Create connection"}
				</Button>
			</DialogFooter>
		</form>
	);
}
